import React, { useEffect, useState } from 'react';

// A code sample in several languages, shown as tabs. The chosen language is
// shared by every sample on the page and remembered between visits.
//
// { type: 'code', title?, variants: [{ language, code, output? }, ...] }

const STORAGE_KEY = 'codeazy:code-language';
const CHANGE_EVENT = 'codeazy:code-language-change';
const DEFAULT_LANGUAGE = 'java';

const LABELS = { java: 'Java', python: 'Python', javascript: 'JavaScript', sql: 'SQL', text: 'Text' };

const readPreference = () => {
  try {
    return window.localStorage.getItem(STORAGE_KEY) || DEFAULT_LANGUAGE;
  } catch {
    return DEFAULT_LANGUAGE;
  }
};

const savePreference = (language) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, language);
  } catch {
    // storage unavailable: the choice still applies for this page
  }
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: language }));
};

const usePreferredLanguage = () => {
  const [language, setLanguage] = useState(readPreference);
  useEffect(() => {
    const onChange = (e) => setLanguage(e.detail);
    window.addEventListener(CHANGE_EVENT, onChange);
    return () => window.removeEventListener(CHANGE_EVENT, onChange);
  }, []);
  return [language, savePreference];
};

const CodeTabs = ({ title, variants }) => {
  const [preferred, setPreferred] = usePreferredLanguage();
  const active = variants.find((v) => v.language === preferred) || variants[0];

  return (
    <div className="code-block code-tabs">
      <div className="code-block-bar code-tabs-bar">
        {title && <span className="code-tabs-title">{title}</span>}
        <div className="code-tabs-list" role="tablist" aria-label="Code language">
          {variants.map((v) => (
            <button
              key={v.language}
              type="button"
              role="tab"
              aria-selected={v === active}
              className={`code-tab ${v === active ? 'active' : ''}`}
              onClick={() => setPreferred(v.language)}
            >
              {LABELS[v.language] || v.language}
            </button>
          ))}
        </div>
      </div>
      <pre className="article-code" role="tabpanel"><code>{active.code}</code></pre>
      {active.output && (
        <div className="code-output">
          <span className="code-output-label">Output</span>
          <pre><code>{active.output}</code></pre>
        </div>
      )}
    </div>
  );
};

export default CodeTabs;
